/* © NXAPP Limited */
import { useState, useRef, Fragment } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import { useAuthContext } from '@/context/AuthContext'
import { UserPlus, Upload, Search, Trash2, UserX, UserCheck } from 'lucide-react'
import EmptyState from '@/components/ui/EmptyState'
import Spinner from '@/components/ui/Spinner'
import StatusBadge from '@/components/ui/StatusBadge'
import { formatDate } from '@/lib/utils'
import type { ContactStatus } from '@/types/database'
import type { ContactRow } from '@/types/rows'
import { useOptimisticList } from '@/hooks/useOptimistic'
import { describeError } from '@/lib/describeError'
import ImportCsvModal from './ImportCsvModal'

const contactSchema = z.object({
  email: z.string().email(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  company: z.string().optional(),
  industry: z.string().optional(),
})
type ContactForm = z.infer<typeof contactSchema>

export default function ContactsPage() {
  const { user } = useAuthContext()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<ContactStatus | ''>('')
  const [showAdd, setShowAdd] = useState(false)
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number } | null>(null)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [error, setError] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)   // contact id whose brand panel is open
  const fileRef = useRef<HTMLInputElement>(null)

  const contactsKey = ['contacts', user?.id, search, statusFilter]
  const { optimisticUpdate, rollback } = useOptimisticList<ContactRow>(contactsKey)

  // A subscription change touches four tables that four different screens read.
  // Invalidate all of them (prefix match) so every view reflects it at once.
  const syncAllViews = () => {
    for (const k of [['contacts'], ['brand-optouts'], ['sequence-enrolments'], ['campaign_members'], ['suppression'], ['contact-memberships']]) {
      qc.invalidateQueries({ queryKey: k })
    }
  }

  const { data: contacts, isLoading } = useQuery({
    queryKey: contactsKey,
    queryFn: async () => {
      let q = supabase.from('contacts').select('*').eq('user_id', user!.id).order('joined_at', { ascending: false })
      if (search) q = q.ilike('email', `%${search}%`)
      if (statusFilter) q = q.eq('status', statusFilter)
      const { data } = await q.limit(100)
      return (data ?? []) as ContactRow[]
    },
    enabled: !!user,
  })

  // The user's brands (one per sending profile identity).
  const { data: brands } = useQuery({
    queryKey: ['brands', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('brands').select('id, name').eq('user_id', user!.id).order('name')
      return (data ?? []) as { id: string; name: string }[]
    },
  })

  // Brand-scoped opt-outs: a contact can be globally SUBSCRIBED yet suppressed
  // for one brand (an `unsubscribes` row). Map contact -> set of opted-out brand
  // ids, so "Subscribed" on this page always means FULLY subscribed and we can
  // show/override each brand individually.
  const { data: brandOptOuts } = useQuery({
    queryKey: ['brand-optouts', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('unsubscribes')
        .select('contact_id, brand_id').eq('user_id', user!.id)
      const map = new Map<string, Set<string>>()
      for (const row of (data ?? []) as { contact_id: string; brand_id: string }[]) {
        if (!map.has(row.contact_id)) map.set(row.contact_id, new Set())
        map.get(row.contact_id)!.add(row.brand_id)
      }
      return map
    },
  })
  const brandName = (id: string) => brands?.find(b => b.id === id)?.name ?? 'a brand'

  const auditIdentity = (c: ContactRow) => ({
    user_id: user!.id,
    contact_id: c.id,
    email: c.email,
    full_name: [c.first_name, c.last_name].filter(Boolean).join(' ') || null,
    company: c.company ?? null,
  })

  // Per-brand override: opt a contact out of, or back into, a single brand.
  const setBrandOptOut = useMutation({
    mutationFn: async ({ contact, brandId, optOut }: { contact: ContactRow; brandId: string; optOut: boolean }) => {
      if (optOut) {
        await supabase.from('unsubscribes').upsert(
          { user_id: user!.id, contact_id: contact.id, brand_id: brandId },
          { onConflict: 'contact_id,brand_id', ignoreDuplicates: true },
        )
        await supabase.from('suppression_events').insert({
          ...auditIdentity(contact), action: 'unsubscribed', actor: 'admin', scope: 'brand',
          reason: 'manual', brand_id: brandId, campaign_name: brandName(brandId),
        })
      } else {
        await supabase.from('unsubscribes').delete()
          .eq('contact_id', contact.id).eq('brand_id', brandId).eq('user_id', user!.id)
        await supabase.from('suppression_events')
          .update({ overridden_at: new Date().toISOString(), overridden_reason: 'Brand resubscribe from Contacts' })
          .eq('contact_id', contact.id).eq('brand_id', brandId).is('overridden_at', null)
      }
    },
    onError: (e) => setError(describeError(e)),
    onSettled: syncAllViews,
  })

  // Segment + campaign memberships per contact (for the two chips columns)
  const { data: memberships } = useQuery({
    queryKey: ['contact-memberships', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const segMap = new Map<string, string[]>()
      const campMap = new Map<string, string[]>()
      const { data: segs } = await supabase
        .from('segments')
        .select('name, segment_contacts ( contact_id )')
        .eq('user_id', user!.id)
      for (const s of (segs ?? []) as { name: string; segment_contacts: { contact_id: string }[] }[]) {
        for (const sc of s.segment_contacts ?? []) {
          const arr = segMap.get(sc.contact_id) ?? []
          arr.push(s.name); segMap.set(sc.contact_id, arr)
        }
      }
      const { data: assigns } = await supabase
        .from('lead_campaign_assignments')
        .select('contact_id, campaigns ( name )')
        .eq('user_id', user!.id)
        .eq('status', 'active')
      for (const a of (assigns ?? []) as unknown as { contact_id: string | null; campaigns: { name: string } | null }[]) {
        if (!a.contact_id || !a.campaigns) continue
        const arr = campMap.get(a.contact_id) ?? []
        if (!arr.includes(a.campaigns.name)) arr.push(a.campaigns.name)
        campMap.set(a.contact_id, arr)
      }
      return { segMap, campMap }
    },
  })

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
  })

  const addContact = useMutation({
    mutationFn: async (data: ContactForm) => {
      const { error } = await supabase.from('contacts').insert({
        ...data,
        user_id: user!.id,
        source: 'manual',
        status: 'SUBSCRIBED',
      })
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
      reset()
      setShowAdd(false)
    },
  })

  const unsubscribe = useMutation({
    mutationFn: async (c: ContactRow) => {
      const { error } = await supabase.from('contacts')
        .update({ status: 'UNSUBSCRIBED', unsubscribed_at: new Date().toISOString() })
        .eq('id', c.id).eq('user_id', user!.id)
      if (error) throw error
      // Contact status is the source of truth: cascade the opt-out to every
      // per-campaign state so no view disagrees. Halt sequences AND campaign
      // memberships immediately, don't wait for a cron tick.
      await supabase.from('sequence_enrollments')
        .update({ status: 'unsubscribed', next_due_at: null })
        .eq('contact_id', c.id).eq('user_id', user!.id).in('status', ['active', 'on_hold'])
      await supabase.from('lead_campaign_assignments')
        .update({ status: 'unsubscribed', unsubscribed_at: new Date().toISOString() })
        .eq('contact_id', c.id).eq('user_id', user!.id).eq('status', 'active')
      // Audit: actor 'admin' — we did this, not the contact.
      await supabase.from('suppression_events').insert({
        ...auditIdentity(c), action: 'unsubscribed', actor: 'admin', scope: 'global', reason: 'manual',
      })
    },
    onMutate: async (c) => {
      setError('')
      const prev = await optimisticUpdate(old =>
        old.map(x => x.id === c.id ? { ...x, status: 'UNSUBSCRIBED' as ContactStatus, unsubscribed_at: new Date().toISOString() } : x)
      )
      return { prev }
    },
    onError: (_e, _c, ctx) => { rollback(ctx?.prev); setError('Failed to unsubscribe — please try again.') },
    onSettled: syncAllViews,
  })

  // Resubscribe clears BOTH forms of opt-out: the global contacts.status flag
  // and any brand-scoped rows in `unsubscribes`. Only do this with real consent
  // — re-adding a contact is not consent — so the caller gates it behind a
  // pointed confirmation.
  const resubscribe = useMutation({
    mutationFn: async (c: ContactRow) => {
      const { error: e1 } = await supabase.from('contacts')
        .update({ status: 'SUBSCRIBED', unsubscribed_at: null })
        .eq('id', c.id).eq('user_id', user!.id)
      if (e1) throw e1
      const { error: e2 } = await supabase.from('unsubscribes')
        .delete().eq('contact_id', c.id).eq('user_id', user!.id)
      if (e2) throw e2
      // Mark the audit trail: the suppression was overridden by us.
      await supabase.from('suppression_events')
        .update({ overridden_at: new Date().toISOString(), overridden_reason: 'Resubscribed from Contacts' })
        .eq('contact_id', c.id).is('overridden_at', null)
    },
    onMutate: async (c) => {
      setError('')
      const prev = await optimisticUpdate(old =>
        old.map(x => x.id === c.id ? { ...x, status: 'SUBSCRIBED' as ContactStatus, unsubscribed_at: null } : x)
      )
      return { prev }
    },
    onError: (_e, _c, ctx) => { rollback(ctx?.prev); setError('Failed to resubscribe — please try again.') },
    onSettled: syncAllViews,
  })

  const deleteContact = useMutation({
    mutationFn: async (c: ContactRow) => {
      // Audit BEFORE deleting so the identity is captured; the FK on
      // suppression_events.contact_id is ON DELETE SET NULL, so the row remains.
      await supabase.from('suppression_events').insert({
        ...auditIdentity(c), action: 'deleted', actor: 'admin', reason: 'manual',
      })
      const { error } = await supabase.from('contacts').delete().eq('id', c.id).eq('user_id', user!.id)
      if (error) throw error
    },
    onMutate: async (c) => {
      setError('')
      const prev = await optimisticUpdate(old => old.filter(x => x.id !== c.id))
      return { prev }
    },
    onError: (_e, _c, ctx) => { rollback(ctx?.prev); setError('Failed to delete — please try again.') },
    onSettled: syncAllViews,
  })

  function handleCsvPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setImportResult(null)
    setImportFile(file)               // opens the column-mapping modal
    if (fileRef.current) fileRef.current.value = ''
  }

  return (
    <div className="space-y-6">
      {importFile && user && (
        <ImportCsvModal
          file={importFile}
          userId={user.id}
          onClose={() => setImportFile(null)}
          onDone={(r) => {
            setImportFile(null)
            setImportResult(r)
            qc.invalidateQueries({ queryKey: ['contacts'] })
            qc.invalidateQueries({ queryKey: ['custom-fields'] })
          }}
        />
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contacts</h1>
          <p className="text-sm text-gray-500 mt-1">{contacts?.length ?? 0} contacts</p>
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleCsvPick} />
          <button onClick={() => fileRef.current?.click()} className="btn-secondary">
            <Upload className="w-4 h-4" /> Import CSV
          </button>
          <button onClick={() => setShowAdd(!showAdd)} className="btn-primary">
            <UserPlus className="w-4 h-4" /> Add contact
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          {error}
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 ml-4">✕</button>
        </div>
      )}

      {importResult && (
        <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
          Imported {importResult.imported} contacts. {importResult.skipped} skipped (invalid/duplicate).
        </div>
      )}

      {showAdd && (
        <div className="card p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Add contact</h3>
          <form onSubmit={handleSubmit(d => addContact.mutate(d))} className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="label">Email *</label>
              <input className="input" placeholder="email@example.com" {...register('email')} />
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
            </div>
            <div>
              <label className="label">First name</label>
              <input className="input" {...register('first_name')} />
            </div>
            <div>
              <label className="label">Last name</label>
              <input className="input" {...register('last_name')} />
            </div>
            <div>
              <label className="label">Company</label>
              <input className="input" {...register('company')} />
            </div>
            <div>
              <label className="label">Industry</label>
              <input className="input" placeholder="e.g. Fintech" {...register('industry')} />
            </div>
            <div className="col-span-2 flex gap-2 justify-end">
              <button type="button" onClick={() => setShowAdd(false)} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={isSubmitting} className="btn-primary">
                {isSubmitting ? 'Adding…' : 'Add contact'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            className="input pl-9"
            placeholder="Search email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className="input w-auto" value={statusFilter} onChange={e => setStatusFilter(e.target.value as ContactStatus | '')}>
          <option value="">All statuses</option>
          <option value="SUBSCRIBED">Subscribed</option>
          <option value="UNSUBSCRIBED">Unsubscribed</option>
          <option value="BOUNCED">Bounced</option>
          <option value="COMPLAINED">Complained</option>
        </select>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : !contacts?.length ? (
          <EmptyState icon={UserPlus} title="No contacts yet" description="Add manually or import a CSV to get started." />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs font-medium text-gray-500 bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Company</th>
                <th className="px-4 py-3 text-left">Segments</th>
                <th className="px-4 py-3 text-left">Campaigns</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Joined</th>
                <th className="px-4 py-3 text-left"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {contacts.map(c => {
                const segs = memberships?.segMap.get(c.id) ?? []
                const camps = memberships?.campMap.get(c.id) ?? []
                const optedOutIds = brandOptOuts?.get(c.id) ?? new Set<string>()
                return (
                <Fragment key={c.id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{c.email}</td>
                  <td className="px-4 py-3 text-gray-600">{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{c.company || '—'}</td>
                  <td className="px-4 py-3">
                    {segs.length ? (
                      <div className="flex flex-wrap gap-0.5 max-w-[160px]">
                        {segs.slice(0, 2).map(n => (
                          <span key={n} className="text-[9px] px-1.5 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-full whitespace-nowrap">{n}</span>
                        ))}
                        {segs.length > 2 && <span className="text-[9px] text-gray-400" title={segs.join(', ')}>+{segs.length - 2}</span>}
                      </div>
                    ) : <span className="text-xs text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {camps.length ? (
                      <div className="flex flex-wrap gap-0.5 max-w-[160px]">
                        {camps.slice(0, 2).map(n => (
                          <span key={n} className="text-[9px] px-1.5 py-0.5 bg-brand-50 text-brand-700 border border-brand-200 rounded-full whitespace-nowrap">{n}</span>
                        ))}
                        {camps.length > 2 && <span className="text-[9px] text-gray-400" title={camps.join(', ')}>+{camps.length - 2}</span>}
                      </div>
                    ) : <span className="text-xs text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={c.status} />
                    {(brands?.length ?? 0) > 0 && (
                      <button
                        onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                        className={`block mt-0.5 text-[10px] ${optedOutIds.size > 0 ? 'text-amber-600' : 'text-gray-400'} hover:underline`}
                        title="See and override this contact's status for each brand"
                      >
                        {optedOutIds.size > 0
                          ? `opted out of ${optedOutIds.size} brand${optedOutIds.size === 1 ? '' : 's'} · manage`
                          : 'per-brand ▾'}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400">{formatDate(c.joined_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {c.status === 'SUBSCRIBED' && (
                        <button
                          onClick={() => confirm('Unsubscribe this contact?') && unsubscribe.mutate(c)}
                          className="p-1 text-gray-400 hover:text-orange-600 rounded" title="Unsubscribe"
                        >
                          <UserX className="w-4 h-4" />
                        </button>
                      )}
                      {c.status === 'UNSUBSCRIBED' && (
                        <button
                          onClick={() => confirm(
                            `Resubscribe ${c.email}?\n\nOnly do this if they have explicitly asked to receive emails again. Re-adding a contact is NOT consent — resubscribing someone who opted out can breach GDPR/PECR and trigger spam complaints.`
                          ) && resubscribe.mutate(c)}
                          className="p-1 text-gray-400 hover:text-green-600 rounded" title="Resubscribe (requires their consent)"
                        >
                          <UserCheck className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => confirm('Delete this contact?\n\nThey will be recorded in the Suppression Log (deleted by you) so this stays auditable.') && deleteContact.mutate(c)}
                        className="p-1 text-gray-400 hover:text-red-600 rounded" title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                {expanded === c.id && (
                  <tr className="bg-gray-50/60">
                    <td colSpan={8} className="px-6 py-3">
                      <p className="text-[11px] font-medium text-gray-500 mb-2">
                        Per-brand subscription for {c.email}
                        {c.status !== 'SUBSCRIBED' && <span className="ml-2 text-red-500">· globally {c.status.toLowerCase()} — no brand will send</span>}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {brands!.map(b => {
                          const out = optedOutIds.has(b.id)
                          return (
                            <div key={b.id} className="flex items-center gap-2 rounded border border-gray-200 bg-white px-2.5 py-1.5">
                              <span className="text-xs text-gray-700">{b.name}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${out ? 'bg-red-50 text-red-600 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                                {out ? 'Opted out' : 'Subscribed'}
                              </span>
                              <button
                                onClick={() => {
                                  const ok = out
                                    ? confirm(`Resubscribe ${c.email} to ${b.name}?\n\nOnly with their consent — this reverses an opt-out.`)
                                    : confirm(`Opt ${c.email} out of ${b.name}?`)
                                  if (ok) setBrandOptOut.mutate({ contact: c, brandId: b.id, optOut: !out })
                                }}
                                disabled={setBrandOptOut.isPending}
                                className={`text-[10px] font-medium px-2 py-0.5 rounded border disabled:opacity-50 ${out
                                  ? 'text-green-700 border-green-300 hover:bg-green-50'
                                  : 'text-gray-500 border-gray-300 hover:bg-gray-100'}`}
                              >
                                {out ? 'Resubscribe' : 'Opt out'}
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              )})}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
